import { useFetcher } from '@remix-run/react'
import { BoldIcon, CheckIcon, ItalicIcon, LinkIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { Alert, Button, IconButton } from '~/ui'
import { cn } from '~/utils/classnames'

import { useRichTextFormatting } from './use-rich-text-formatting'

interface LegalRichTextEditorProps {
  fieldName: 'privacyPolicy' | 'termsAndConditions' | 'returnPolicy'
  initialValue: string
  placeholder?: string
  className?: string
}

export const LegalRichTextEditor = ({
  fieldName,
  initialValue,
  className,
}: LegalRichTextEditorProps) => {
  const { t } = useTranslation()
  const shop = useShop()
  const fetcher = useFetcher()
  const editorRef = useRef<HTMLDivElement>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [isEmpty, setIsEmpty] = useState(false)

  // Memoize the initial HTML so it never changes after first render
  // This prevents React from resetting the contentEditable on re-renders
  const initialHtml = useRef(initialValue)
  const dangerousHtml = useRef({ __html: initialHtml.current })

  const isLoading = fetcher.state === 'submitting'
  const isIdle = fetcher.state === 'idle'
  const hasData = fetcher.data != null
  const hasError =
    hasData &&
    typeof fetcher.data === 'object' &&
    'error' in (fetcher.data as object)
  const isSuccess = isIdle && hasData && !hasError

  // Check if content is empty (only whitespace or empty tags)
  const checkIfEmpty = useCallback((html: string) => {
    const textContent = html.replace(/<[^>]*>/g, '').trim()
    return textContent.length === 0
  }, [])

  // Handle content changes
  const handleInput = useCallback(() => {
    if (!editorRef.current) return
    const currentValue = editorRef.current.innerHTML
    const hasChanged = currentValue !== initialHtml.current
    setIsDirty(hasChanged)
    setIsEmpty(checkIfEmpty(currentValue))
  }, [checkIfEmpty])

  // Reset dirty state after successful save
  useEffect(() => {
    if (isSuccess && editorRef.current) {
      initialHtml.current = editorRef.current.innerHTML
      setIsDirty(false)
    }
  }, [isSuccess])

  // Use the formatting hook
  const {
    applyFormatting,
    applyLink,
    isOpen,
    floatingStyles,
    refs,
    getFloatingProps,
    context,
  } = useRichTextFormatting({
    editorRef,
  })

  const handleSave = () => {
    if (!editorRef.current) return

    const value = editorRef.current.innerHTML
    fetcher.submit(
      { [fieldName]: value },
      {
        method: 'PATCH',
        action: makeShopOwnPath(shop, '/settings?index'),
      }
    )
  }

  const handleLinkClick = () => {
    const url = prompt('Enter URL:')
    if (url?.trim()) {
      applyLink(url)
    }
  }

  return (
    <div className={className}>
      <div className="relative">
        {/* Content editable div */}
        <div
          ref={editorRef}
          contentEditable
          className={cn(
            'transition',
            'min-h-[200px] w-full rounded-md p-4',
            'bg-foreground/2 hover:bg-foreground/4',
            isEmpty && 'ring-destructive ring-2'
          )}
          dangerouslySetInnerHTML={dangerousHtml.current}
          onInput={handleInput}
          suppressContentEditableWarning
          suppressHydrationWarning
        />

        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={cn(
              'bg-background border-border/8 z-50 flex items-center gap-1 rounded-md border p-1 shadow-lg',
              context.middlewareData.hide?.referenceHidden && 'invisible'
            )}
            {...getFloatingProps()}
            onMouseDown={(e) => {
              // Prevent toolbar clicks from affecting text selection
              e.preventDefault()
            }}
          >
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Bold"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                applyFormatting('strong')
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <BoldIcon />
            </IconButton>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Italic"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                applyFormatting('em')
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <ItalicIcon />
            </IconButton>

            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Insert link"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleLinkClick()
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <LinkIcon />
            </IconButton>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col items-end justify-between gap-1 sm:flex-row sm:items-center">
        {hasError ? (
          <Alert type="error" title="Something went wrong, please try again." />
        ) : isEmpty ? (
          <Alert
            type="error"
            title={t('page.settings.legal.content_required')}
          />
        ) : isDirty ? (
          <p className="text-foreground text-xs">
            {t('page.settings.shipping.form.unsaved_changes')}
          </p>
        ) : (
          <div />
        )}
        <Button
          onClick={handleSave}
          isLoading={isLoading}
          disabled={!isDirty || isEmpty}
        >
          {isSuccess && !isDirty && <CheckIcon />}
          {isSuccess && !isDirty ? t('action.saved') : t('action.save')}
        </Button>
      </div>
    </div>
  )
}
